import { Pill } from '@/components/shared/Pill';
import { CircularProgress, Menu, MenuItem } from '@mui/material';
import { useRef, useState } from 'react';
import { PillStatus } from '@/components/shared/Pill/types';
import { iconSizeConfig } from '@/components/shared/Pill/constants';

export const PillMenu = ({
  options,
  initialValue,
  loading = false,
  loadingLabel = 'Updating...',
  onChange = () => {},
  readOnly = false,
  getPillStatus = () => 'info',
  menuAnchorOrigin = { vertical: 'bottom', horizontal: 'right' },
  menuTransformOrigin = { vertical: 'top', horizontal: 'right' },
}: {
  options: string[];
  initialValue: string;
  loading: boolean;
  loadingLabel: string;
  onChange: (status: string) => void;
  readOnly?: boolean;
  getPillStatus?: (status: string) => PillStatus;
  menuAnchorOrigin?: { vertical: 'top' | 'bottom'; horizontal: 'left' | 'right' };
  menuTransformOrigin?: { vertical: 'top' | 'bottom'; horizontal: 'left' | 'right' };
}) => {
  const [status, setStatus] = useState<string>(initialValue);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | undefined>(undefined);
  const mainRef = useRef(null);

  const handleMenuClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setAnchorElement(mainRef.current ?? undefined);
  };

  const handleClose = () => {
    setAnchorElement(undefined);
  };

  const handleChange = (status: string) => {
    setStatus(status);
    onChange(status);
    handleClose();
  };

  return (
    <>
      <div ref={mainRef} data-testid="pill-menu">
        <Pill
          disableRipple
          label={loading ? loadingLabel : status}
          showDropdownIcon={!loading && !readOnly}
          type={readOnly ? 'readonly' : 'clickable'}
          onDropdownClick={handleMenuClick}
          onClick={handleMenuClick}
          status={getPillStatus(status)}
          showIcon={loading}
          icon={
            loading ? (
              <CircularProgress size={`calc(${iconSizeConfig['small']} * 0.8)`} />
            ) : undefined
          }
          state={loading ? 'pressed' : 'default'}
          disabled={loading}
        />
      </div>
      <Menu
        elevation={0}
        anchorEl={anchorElement}
        open={Boolean(anchorElement)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: menuAnchorOrigin.vertical,
          horizontal: menuAnchorOrigin.horizontal,
        }}
        transformOrigin={{
          vertical: menuTransformOrigin.vertical,
          horizontal: menuTransformOrigin.horizontal,
        }}
        sx={{
          '& .MuiMenu-paper': {
            marginBottom: menuTransformOrigin.vertical === 'bottom' ? '1rem' : '0',
            marginTop: menuTransformOrigin.vertical === 'top' ? '1rem' : '0',
          },
        }}
      >
        {options.map((status) => (
          <MenuItem key={status} onClick={() => handleChange(status)}>
            {status}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
